import { createSelector, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { authApiSlice, isLoginMatch, Contact } from './auth-api.slice';
import { isRegularLogoutMatch, isLogoutWithNoticeMatch } from './account-api.slice';

export type LoginStatus = 'LOGGED_IN' | 'LOGGED_OUT_WITH_NOTICE' | 'LOGGED_OUT';

export interface Application {
    applicationId: string;
    isEnvApp: boolean;
}

interface LoginState {
    status: LoginStatus;
    userEmail: string;
    contacts: Contact[];
    isVcrEnvironment: boolean;
    application?: Application;
}

const initialState: LoginState = {
    status: 'LOGGED_OUT',
    userEmail: '',
    contacts: [],
    isVcrEnvironment: false,
    application: undefined
};

function logoutReducer() {
    return {
        ...initialState,
        status: 'LOGGED_OUT'
    };
}

function logoutWithNoticeReducer() {
    return {
        ...initialState,
        status: 'LOGGED_OUT_WITH_NOTICE'
    };
}

export const loginSlice = createSlice({
    name: 'login',
    initialState: () => ({
        ...initialState,
        status: localStorage.getItem('loggedIn') === 'true' ? 'LOGGED_IN' : 'LOGGED_OUT'
    }),
    reducers: {
        logout: logoutReducer,
        logoutWithNotice: logoutWithNoticeReducer
    },
    extraReducers: builder => {
        builder
            .addMatcher(isLoginMatch, () => {
                return {
                    ...initialState,
                    status: 'LOGGED_IN'
                };
            })
            .addMatcher(
                isAnyOf(
                    authApiSlice.endpoints.verifyLoginTotp.matchFulfilled,
                    authApiSlice.endpoints.trustedDevice.matchFulfilled,
                    authApiSlice.endpoints.enrollmentPhoneInit.matchFulfilled
                ),
                (state, action) => {
                    const { appId, isEnvApp } = action.payload;
                    state.application = appId
                        ? {
                              applicationId: appId,
                              isEnvApp: !!isEnvApp
                          }
                        : undefined;
                }
            )
            .addMatcher(isRegularLogoutMatch, logoutReducer)
            .addMatcher(isLogoutWithNoticeMatch, logoutWithNoticeReducer)
            .addMatcher(authApiSlice.endpoints.initEmailLogin.matchFulfilled, (state, action) => {
                state.userEmail = action.meta.arg.originalArgs.email;
            })
            .addMatcher(authApiSlice.endpoints.recoverAccount.matchFulfilled, (state, action) => {
                const { contacts } = action.payload.maskedContact;
                state.isVcrEnvironment = action.payload.isVcrEnvironment;
                state.contacts = contacts;
            })
            .addMatcher(authApiSlice.endpoints.loginPassword.matchFulfilled, (state, action) => {
                const { contacts } = action.payload;
                if (contacts) {
                    state.contacts = contacts;
                }
            });
    }
});

export const loginActions = loginSlice.actions;
export const selectSelf = (rootState: any): LoginState => rootState[loginSlice.name];
export const selectUserEmail = createSelector(selectSelf, state => state.userEmail);
export const selectLoginState = createSelector(selectSelf, state => state);
export const selectFirstEmailContact = createSelector(selectSelf, state =>
    state.contacts.find(contact => contact.contactType === 'email')
);
export const selectIsVcrEnvironment = createSelector(selectSelf, state => state.isVcrEnvironment);
